import { useEffect, useRef, useState } from "react";
import { Occurrence, OccurrenceDispatch, OccurrenceReview, OccurrenceWithTimeSpent } from "../models/occurrence";
import { fetchOccurenceList, fetchOccurenceListWithTimeSpent, fetchOccurrenceStepAvailable } from "../api/fetchOccurence";
import { getOccurrenceStepKeyOfValue, OccurrenceStepEnum, OccurrenceStepLabelEnum } from "../enums/occurrenceStep";
import { cancelOccurrenceDispatch, changeOccurrenceStep, createOccurrence, createOccurrenceDispatch, finishOccurrence, getOccurrenceReview, removeOccurrenceDispatch, reviewOccurrenceStep, updateOccurrence, updateOccurrenceDispatch } from "../api/occurrence";
import { getAssetIdByChassis, getChassisListFromAssetIds } from "../utils/assetUtils";
import useAssets from "./useAssets";
import { signRevision } from "../api/signedRevision";
import { useSelector } from "react-redux";
import { getUserProfile } from "../configuration/login/loginSlice";
import { fetchComplementaryAssetsDetails } from "../api/fetchAssets";
import { fetchUser } from "../api/fetchUser";
import { sendBotMessage } from "../services/amityService";
import { getFinalizedFilterValue } from "../utils/kanbanUtils";
import { Step } from "../models/step";
import { fetchSteps } from "../api/step";

function useOccurrence(userRole: 'TOWER' | 'CONSULTANT' | 'MANAGER' | 'PARTNERSHIP', finalizedFilter?: boolean, assetIds?: string[], chassisList?: string[]) {

    const userProfile = useSelector(getUserProfile);
    const limit = 50;

    /**
     * Filtros
    */
    const [criticalityFilter, setCriticalityFilter] = useState<string>("");
    const [typeFilter, setTypeFilter] = useState<string[]>([]);
    const [isAlertFilter, setIsAlertFilter] = useState<string>("");
    const [chassisFilter, setChassisFilter] = useState<string>("");
    const [searchFieldName, setSearchFieldName] = useState<string>("CHASSIS");
    const [searchFieldValue, setSearchFieldValue] = useState<string>("");
    const [chassisListFilter, setChassisListFilter] = useState<string[]>([]);
    const [chassisListFilterByStep, setChassisListFilterByStep] = useState<string[]>([]);
    const [startDateFilter, setStartDateFilter] = useState<{ start?: string, end?: string } | null>(null);
    const [updateDateFilter, setUpdateDateFilter] = useState<{ start?: string, end?: string } | null>(null);
    const [finishedFilter, setFinishedFilter] = useState<string[]>(['false']);
    const [dealershipFilter, setDealershipFilter] = useState<string[]>([]);
    const [regionalFilter, setRegionalFilter] = useState<string[]>([]);
    const [cellFilter, setCellFilter] = useState<string[]>([]);
    const [cpvNameFilter, setCpvNameFilter] = useState<string | undefined>(undefined);
    const [stepFilter, setStepFilter] = useState<OccurrenceStepEnum | null>(null);
    const [stepTypeCurrentFilter, _setStepTypeCurrentFilter] = useState<OccurrenceStepEnum | null>(null);
    const stepTypeCurrentFilterRef = useRef<OccurrenceStepEnum | null>(null);
    const setStepTypeCurrentFilter = (value: OccurrenceStepEnum | null) => {
        stepTypeCurrentFilterRef.current = value;
        _setStepTypeCurrentFilter(value);
    };

    /**
     * Estado
    */
    const [viewType, setViewType] = useState<"card" | "list">("card");
    const [firstLoad, setFirstLoad] = useState<boolean>(true);
    const assetIdsEverPopulated = useRef(false);
    const requestIdRef = useRef(0);
    const hasInitiallyLoaded = useRef(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingMoreData, setLoadingMoreData] = useState<boolean>(false);
    const [page, setPage] = useState<number>(0);

    /**
     * Listas
    */
    const { allAssets } = useAssets();
    const [occurrenceList, setOccurrenceList] = useState<Occurrence[]>([]);
    const [totalOccurrences, setTotalOccurrences] = useState<number>(0);
    const [totalInProgress, setTotalInProgress] = useState<number>(0);
    const [occurrenceWithTimeSpentList, _setOccurrenceWithTimeSpentList] = useState<OccurrenceWithTimeSpent[]>([]);
    const [rawCurrentOccurrenceWithTimeSpentList, setRawCurrentOccurrenceWithTimeSpentList] = useState<OccurrenceWithTimeSpent[]>([]);
    const occurrenceWithTimeSpentListRef = useRef<OccurrenceWithTimeSpent[]>([]);
    const setOccurrenceWithTimeSpentList = (list: OccurrenceWithTimeSpent[]) => {
        occurrenceWithTimeSpentListRef.current = list;
        _setOccurrenceWithTimeSpentList(list);
    };
    const [finalizedOccurrenceWithTimeSpentList, setFinalizedOccurrenceWithTimeSpentList] = useState<OccurrenceWithTimeSpent[]>([]);
    const [uniqueAssetIds, setUniqueAssetIds] = useState<string[]>([]);
    const [occurrenceStepAvailable, setOccurrenceStepAvailable] = useState<string[]>([]);
    const [steps, setSteps] = useState<Step[]>([]);

    const [debouncedAssetIds, setDebouncedAssetIds] = useState<string[] | undefined>(assetIds);
    const assetIdsTimerRef = useRef<NodeJS.Timeout>();

    useEffect(() => {
        if (assetIdsTimerRef.current) {
            clearTimeout(assetIdsTimerRef.current);
        }

        if (assetIds && assetIds.length === 0) {
            setDebouncedAssetIds(assetIds);
            return;
        }

        assetIdsTimerRef.current = setTimeout(() => {
            setDebouncedAssetIds(assetIds);
        }, 300);

        return () => {
            if (assetIdsTimerRef.current) {
                clearTimeout(assetIdsTimerRef.current);
            }
        };
    }, [assetIds]);

    const proceedWithFilters = () => {
        if (userRole === 'PARTNERSHIP') return hasInitiallyLoaded.current;
        if (debouncedAssetIds !== undefined && debouncedAssetIds.length === 0) return false;
        return debouncedAssetIds && debouncedAssetIds.length > 0;
    };

    useEffect(() => {
        const fetchStepsAvailable = async () => {
            const steps = await fetchOccurrenceStepAvailable();
            setOccurrenceStepAvailable(steps.map(step => getOccurrenceStepKeyOfValue(step)));

            const stepsWithElapsedHours = await fetchSteps();
            setSteps(stepsWithElapsedHours);
        }

        fetchStepsAvailable();
    }, []);

    const getChassisList = (assetIds: string[]) => {
        return getChassisListFromAssetIds(allAssets, assetIds.filter(Boolean)).filter(Boolean);
    }

    useEffect(() => {
        setLoading(true);

        const localUniqueAssetIds = Array.from(new Set(debouncedAssetIds));
        setUniqueAssetIds(prev => prev = localUniqueAssetIds);

        if (
            debouncedAssetIds && debouncedAssetIds.length > 0 &&
            userRole !== 'PARTNERSHIP'
        ) setChassisListFilter(getChassisList(localUniqueAssetIds));

        if (assetIds && assetIds.length > 0) {
            assetIdsEverPopulated.current = true;
        }

        if (
            debouncedAssetIds && debouncedAssetIds.length === 0 && assetIdsEverPopulated.current
        ) {
            requestIdRef.current += 1;
            setChassisListFilter([]);
            setOccurrenceList([]);
            setOccurrenceWithTimeSpentList([]);
            setFinalizedOccurrenceWithTimeSpentList([]);
            setTotalOccurrences(0);
            setTotalInProgress(0);
            setPage(0);
            setLoading(false);
            setLoadingMoreData(false);
            return;
        }

        if (userRole === 'PARTNERSHIP') {
            hasInitiallyLoaded.current = true;
            getOccurrenceList({ localPage: 0 });
        }

        if (debouncedAssetIds && debouncedAssetIds.length > 0 && firstLoad) {
            setFirstLoad(false);
        }
    }, [debouncedAssetIds]);

    useEffect(() => {
        if (chassisList && chassisList.length > 0) {
            setChassisListFilter(chassisList);
        } else if (chassisList && chassisList.length === 0) {
            setChassisListFilter([]);
        }
    }, [chassisList]);

    const getChassisListFilter = (chassisFilter: string) => {
        return chassisFilter === '' && uniqueAssetIds && uniqueAssetIds.length > 0 ?
            getChassisList(uniqueAssetIds) :
            chassisFilter ? [chassisFilter] : chassisListFilter ?? undefined
    }

    useEffect(() => {
        if (chassisListFilter && chassisListFilter.length > 0 && proceedWithFilters()) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: chassisListFilter
            });
        } else if (chassisListFilter && chassisListFilter.length === 0) {
            setOccurrenceList([]);
            setOccurrenceWithTimeSpentList([]);
            setFinalizedOccurrenceWithTimeSpentList([]);
            setTotalOccurrences(0);
            setPage(0);
        }
    }, [chassisListFilter]);

    useEffect(() => {
        if (proceedWithFilters()) {
            getOccurrenceList({localPage: 0});
        }
    }, [chassisListFilterByStep]);

    useEffect(() => {
        if ((chassisFilter || chassisFilter === "") && proceedWithFilters()) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: searchFieldValue ? undefined : getChassisListFilter(chassisFilter)
            });
        }
    }, [chassisFilter, searchFieldValue]);

    useEffect(() => {
        if (criticalityFilter && proceedWithFilters()) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: getChassisListFilter(chassisFilter)
            });
        }
    }, [criticalityFilter]);

    useEffect(() => {
        if (typeFilter && proceedWithFilters()) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: getChassisListFilter(chassisFilter)
            });
            // getQuantityAnalytics({ localChassisListFilter: getChassisListFilter(chassisFilter) });
        }
    }, [typeFilter]);

    useEffect(() => {
        if (isAlertFilter && assetIds && assetIds.length > 0) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: getChassisListFilter(chassisFilter)
            });
        }
    }, [isAlertFilter]);

    useEffect(() => {
        if (finishedFilter.length >= 0 && proceedWithFilters()) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: getChassisListFilter(chassisFilter)
            });
        }
    }, [finishedFilter]);

    useEffect(() => {
        if (regionalFilter && proceedWithFilters()) {
            getOccurrenceList({ localPage: 0, localChassisListFilter: getChassisListFilter(chassisFilter) });
        }
    }, [regionalFilter]);

    useEffect(() => {
        if (cellFilter && proceedWithFilters()) {
            getOccurrenceList({ localPage: 0, localChassisListFilter: getChassisListFilter(chassisFilter) });
        }
    }, [cellFilter]);

    useEffect(() => {
        if (proceedWithFilters()) {
            getOccurrenceList({ localPage: 0, localChassisListFilter: getChassisListFilter(chassisFilter) });
        }
    }, [cpvNameFilter]);

    useEffect(() => {
        if (dealershipFilter.length >= 0 && proceedWithFilters()) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: getChassisListFilter(chassisFilter)
            });
        }
    }, [dealershipFilter]);

    useEffect(() => {
        if (
            startDateFilter && (
                proceedWithFilters() &&
                startDateFilter?.start && startDateFilter?.end ||
                !startDateFilter?.start && !startDateFilter?.end)
        ) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: getChassisListFilter(chassisFilter)
            });
        }
    }, [startDateFilter]);

    useEffect(() => {
        if (
            updateDateFilter && (
                proceedWithFilters() &&
                updateDateFilter?.start && updateDateFilter?.end ||
                !updateDateFilter?.start && !updateDateFilter?.end)
        ) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: getChassisListFilter(chassisFilter)
            });
        }
    }, [updateDateFilter]);

    useEffect(() => {
        if (proceedWithFilters()) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: getChassisListFilter(chassisFilter)
            });
        }
    }, [stepFilter]);

    useEffect(() => {
        if (proceedWithFilters()) {
            getOccurrenceList({ localPage: 0 });
        }
    }, [stepTypeCurrentFilter]);

    const searchByChassis = (searchTerm: string, fieldName?: string) => {
        setSearchFieldName(fieldName ?? 'CHASSIS');
        setSearchFieldValue(searchTerm);
        setChassisFilter(searchTerm);
    };

    // ---------- Helpers p/ exportação (não afetam a UI) ----------
    const buildFilters = (override?: { chassisListFilter?: string[] }) => ({
        criticalityFilter:
            criticalityFilter === "undefined" ? undefined : criticalityFilter,
        typeFilter: typeFilter.length > 0 ? typeFilter : undefined,
        chassisListFilter:
            override?.chassisListFilter ??
            (chassisFilter === "" && uniqueAssetIds && uniqueAssetIds.length > 0
                ? getChassisList(uniqueAssetIds)
                : chassisFilter
                    ? [chassisFilter]
                    : chassisListFilter ?? undefined),
        startDateFilter: startDateFilter?.start,
        endDateFilter: startDateFilter?.end,
        updateDateFilter: updateDateFilter?.start,
        updateDateFilterEnd: updateDateFilter?.end,
        dealershipFilter: dealershipFilter,
        regionalFilter: regionalFilter.length > 0 ? regionalFilter : undefined,
        cellFilter: cellFilter.length > 0 ? cellFilter : undefined,
    });

    /** Busca página “crua” para o exportador */
    const fetchOccurrencePage = async ({
        page,
        size = limit,
    }: {
        page: number;
        size?: number;
    }): Promise<Occurrence[]> => {
        const data = await fetchOccurenceList(
            buildFilters(),
            userRole,
            page,
            size,
            getFinalizedFilterValue(finishedFilter, finalizedFilter)
        );
        return data.occurrences;
    };

    /** Reseta paginação da UI quando necessário (ex.: antes de novo carregamento total) */
    const resetPaging = () => {
        setPage(0);
    };

    const getOccurrenceList = async ({ localPage, localChassisListFilter, localStepTypeCurrentFilter }: { localPage?: number, localChassisListFilter?: string[], localStepTypeCurrentFilter?: OccurrenceStepEnum | null }) => {
        if (!chassisFilter &&
            (!localChassisListFilter || localChassisListFilter.length === 0) &&
            userRole !== 'PARTNERSHIP' &&
            (!uniqueAssetIds || uniqueAssetIds.length === 0)) {
            return;
        }

        requestIdRef.current += 1;
        const currentRequestId = requestIdRef.current;

        if (localPage === 0 || page === 0) setLoading(true);
        else setLoadingMoreData(true);

        // Se o usuário for consultante e a quantidade de chassis for igual a quantidade total de assets na conta, o chassisListFilter deve ser vazio
        if (userRole === 'CONSULTANT' && ((localChassisListFilter && localChassisListFilter.length === allAssets.length) || !localChassisListFilter)) {
            localChassisListFilter = [];
        }

        const filters = {
            criticalityFilter: criticalityFilter === "undefined" ? undefined : criticalityFilter,
            typeFilter: typeFilter.length > 0 ? typeFilter : undefined,
            isAlertFilter: isAlertFilter === "undefined" ? undefined : isAlertFilter,
            chassisFilter: !searchFieldValue ? (chassisFilter || undefined) : undefined,
            chassisListFilter: !searchFieldValue ? (localChassisListFilter ?? getChassisListFilter(chassisFilter)) : undefined,
            chassisListFilterByStep: chassisListFilterByStep,
            startDateFilter: startDateFilter?.start,
            endDateFilter: startDateFilter?.end,
            updateDateFilter: updateDateFilter?.start,
            updateDateFilterEnd: updateDateFilter?.end,
            dealershipFilter: dealershipFilter,
            regionalFilter: regionalFilter.length > 0 ? regionalFilter : undefined,
            cellFilter: cellFilter.length > 0 ? cellFilter : undefined,
            stepFilter,
            stepTypeCurrentFilter: localStepTypeCurrentFilter !== undefined ? localStepTypeCurrentFilter : stepTypeCurrentFilterRef.current,
            searchFieldName: searchFieldValue ? searchFieldName : undefined,
            searchFieldValue: searchFieldValue || undefined,
            cpvNameFilter: cpvNameFilter || undefined,
        }

        const isFirstPage = localPage === 0 || (localPage === undefined && page === 0);

        try {
            const data = await fetchOccurenceList(
                filters, userRole, localPage ?? page, limit, getFinalizedFilterValue(finishedFilter, finalizedFilter)
            );

            if (currentRequestId !== requestIdRef.current) return;

            const finalizedFilterValue = getFinalizedFilterValue(finishedFilter, finalizedFilter);

            if (isFirstPage) {
                let allOccurrenceWithTimeSpent = await fetchOccurenceListWithTimeSpent(
                    filters, userRole, finalizedFilterValue ?? undefined
                );


                const extraFinalizedTimeSpent = finalizedFilterValue === false
                    ? await fetchOccurenceListWithTimeSpent(filters, userRole, true)
                    : null;

                if (currentRequestId !== requestIdRef.current) return;

                if (cpvNameFilter) {
                    const hasCpvData =
                        allOccurrenceWithTimeSpent.current.some(i => i.cpvName) ||
                        allOccurrenceWithTimeSpent.finalized.some(i => i.cpvName);
                    if (hasCpvData) {
                        allOccurrenceWithTimeSpent = {
                            current: allOccurrenceWithTimeSpent.current.filter(i => i.cpvName === cpvNameFilter),
                            finalized: allOccurrenceWithTimeSpent.finalized.filter(i => i.cpvName === cpvNameFilter),
                        };
                    }
                }

                const timeSpentList = [
                    ...(finalizedFilterValue !== true ? allOccurrenceWithTimeSpent.current : []),
                    ...(finalizedFilterValue !== false ? allOccurrenceWithTimeSpent.finalized : []),
                ];

                const clTimeSpent = await fetchOccurenceListWithTimeSpent(
                    { ...filters, typeFilter: ['CLAIM'] },
                    userRole,
                    undefined
                );
                const clIds = new Set([
                    ...clTimeSpent.current.map(item => item.occurrenceId),
                    ...clTimeSpent.finalized.map(item => item.occurrenceId),
                ].filter(Boolean));

                const enrichWithCL = (items: OccurrenceWithTimeSpent[]) =>
                    items.map(item => ({
                        ...item,
                        occurrenceType: clIds.has(item.occurrenceId) ? 'CL' : item.occurrenceType,
                    }));

                const finalizedForIndicators = finalizedFilterValue === false
                    ? (extraFinalizedTimeSpent?.finalized ?? allOccurrenceWithTimeSpent.finalized)
                    : allOccurrenceWithTimeSpent.finalized;

                setOccurrenceWithTimeSpentList(enrichWithCL(timeSpentList));
                setFinalizedOccurrenceWithTimeSpentList(enrichWithCL(finalizedForIndicators));
                setRawCurrentOccurrenceWithTimeSpentList(allOccurrenceWithTimeSpent.current);

                setOccurrenceList(() =>
                    data.occurrences.map(occurrence => {
                        const ts = timeSpentList.find(el => el.occurrenceId === occurrence.id);
                        return { ...occurrence, timeSpent: ts?.timeSpent ?? 0 };
                    })
                );

                addAccountNameAndSaleDate(data.occurrences);
            } else {
                setOccurrenceList(prev => [
                    ...prev,
                    ...data.occurrences.map(occurrence => {
                        const ts = occurrenceWithTimeSpentListRef.current.find(el => el.occurrenceId === occurrence.id);
                        return { ...occurrence, timeSpent: ts?.timeSpent ?? 0 };
                    })
                ]);

                addAccountNameAndSaleDate(data.occurrences);
            }

            setTotalOccurrences(data.total);
            setTotalInProgress(data.totalInProgress);
            setPage(localPage !== undefined ? localPage + 1 : page + 1);
        } finally {
            if (currentRequestId === requestIdRef.current) {
                setLoading(false);
                setLoadingMoreData(false);
            }
        }
    }

    const refreshCurrentData = async () => {
        const filters = {
            criticalityFilter: criticalityFilter === "undefined" ? undefined : criticalityFilter,
            typeFilter: typeFilter.length > 0 ? typeFilter : undefined,
            chassisListFilter: getChassisListFilter(chassisFilter),
            startDateFilter: startDateFilter?.start,
            endDateFilter: startDateFilter?.end,
            updateDateFilter: updateDateFilter?.start,
            updateDateFilterEnd: updateDateFilter?.end,
            dealershipFilter: dealershipFilter,
            regionalFilter: regionalFilter.length > 0 ? regionalFilter : undefined,
            cellFilter: cellFilter.length > 0 ? cellFilter : undefined,
            stepFilter,
            cpvNameFilter: cpvNameFilter || undefined,
        }

        try {
            const finalizedFilterValue = getFinalizedFilterValue(finishedFilter, finalizedFilter);
            const [data, allOccurrenceWithTimeSpent] = await Promise.all([
                fetchOccurenceList(filters, userRole, 0, limit * page, finalizedFilterValue),
                fetchOccurenceListWithTimeSpent(filters, userRole, finalizedFilterValue ?? undefined),
            ]);

            const timeSpentList = [
                ...(finalizedFilterValue !== true ? allOccurrenceWithTimeSpent.current : []),
                ...(finalizedFilterValue !== false ? allOccurrenceWithTimeSpent.finalized : []),
            ];

            setOccurrenceList(data.occurrences);
            setTotalOccurrences(data.total);
            setOccurrenceWithTimeSpentList(timeSpentList);
            setFinalizedOccurrenceWithTimeSpentList(allOccurrenceWithTimeSpent.finalized);

            addAccountNameAndSaleDate(data.occurrences);
        } catch (error) {
            console.error("Erro ao atualizar dados:", error);
        }
    }

    const reloadOccurrenceList = async (occurrenceIdToRemove?: string, occurrenceIdToAdd?: string) => {
        setLoading(true);
        const fetchData = async (page: number) => {
            const filters = {
                criticalityFilter: criticalityFilter === "undefined" ? undefined : criticalityFilter,
                chassisFilter,
                chassisListFilter: chassisFilter === '' && uniqueAssetIds ?
                    getChassisList(uniqueAssetIds) :
                    chassisFilter ?
                        [chassisFilter] :
                        undefined,
                startDateFilter: startDateFilter?.start,
                endDateFilter: startDateFilter?.end,
                updateDateFilter: updateDateFilter?.start,
                updateDateFilterEnd: updateDateFilter?.end,
                regionalFilter: regionalFilter.length > 0 ? regionalFilter : undefined,
                cellFilter: cellFilter.length > 0 ? cellFilter : undefined,
                stepFilter: stepFilter ?? undefined,
                cpvNameFilter: cpvNameFilter || undefined,
            }
            const data = await fetchOccurenceList(filters, userRole, page, limit, getFinalizedFilterValue(finishedFilter, finalizedFilter));
            const allOccurrenceWithTimeSpent = await fetchOccurenceListWithTimeSpent(filters, userRole, getFinalizedFilterValue(finishedFilter, finalizedFilter) ?? undefined);
            const finalizedFilterValue = getFinalizedFilterValue(finishedFilter, finalizedFilter);
            const occurrenceWithTimeSpentList = [
                ...(finalizedFilterValue !== true ? allOccurrenceWithTimeSpent.current : []),
                ...(finalizedFilterValue !== false ? allOccurrenceWithTimeSpent.finalized : []),
            ];

            setOccurrenceWithTimeSpentList(occurrenceWithTimeSpentList);
            setFinalizedOccurrenceWithTimeSpentList(allOccurrenceWithTimeSpent.finalized);

            return data.occurrences;
        }

        const updatedOccurrenceList: Occurrence[] = [];
        for (let i = 0; i < page; i++) {
            const data = await fetchData(i);
            updatedOccurrenceList.push(...data);
        }

        let newOccurrenceList = [...occurrenceList];

        const occurrenceToAdd = occurrenceIdToAdd ? updatedOccurrenceList.find(occurrenceEl => occurrenceEl.id === occurrenceIdToAdd) : null;
        if (occurrenceToAdd) {
            newOccurrenceList.push(occurrenceToAdd);
        }

        setOccurrenceList([
            ...newOccurrenceList
                .filter(occurrenceEl => occurrenceEl.id !== occurrenceIdToRemove)
                .map(occurrenceEl => updatedOccurrenceList.find(updatedOccurrenceEl => updatedOccurrenceEl.id === occurrenceEl.id) ?? occurrenceEl),
        ]);

        setLoading(false);

        addAccountNameAndSaleDate([
            ...newOccurrenceList
                .filter(occurrenceEl => occurrenceEl.id !== occurrenceIdToRemove)
                .map(occurrenceEl => updatedOccurrenceList.find(updatedOccurrenceEl => updatedOccurrenceEl.id === occurrenceEl.id) ?? occurrenceEl),
        ]);
    }

    const addAccountNameAndSaleDate = async (data: Occurrence[]) => {
        const assetIds = data.map(occurrence => getAssetIdByChassis(allAssets, occurrence.vehicleChassis)).filter(el => el !== undefined && el !== null);
        const complementaryAssetsDetails = await fetchComplementaryAssetsDetails(assetIds);

        setOccurrenceList(prev => prev.map(occurrence => {
            const vehicleData = complementaryAssetsDetails.find(el => el.id === getAssetIdByChassis(allAssets, occurrence.vehicleChassis));
            return {
                ...occurrence,
                accountName: vehicleData?.accountName ?? '-',
                vehicleSaleDate: vehicleData?.saleDate ? new Date(vehicleData.saleDate) : undefined,
            };
        }));
    }

    const changeStep = async (occurrence: Occurrence, fromStepId: number, to: OccurrenceStepEnum): Promise<Occurrence | null> => {
        const toStepEnum = Object.keys(OccurrenceStepEnum).find(key => OccurrenceStepEnum[key as keyof typeof OccurrenceStepEnum] === to) as keyof typeof OccurrenceStepEnum;

        try {
            const updatedOccurrence = await changeOccurrenceStep(occurrence, fromStepId, to);
            if (!updatedOccurrence) return null;

            setOccurrenceList([
                ...occurrenceList
                    .map(occurrenceEl => occurrenceEl.id === occurrence.id ? updatedOccurrence : occurrenceEl),
            ]);

            if (occurrence.createdByUserProfileId) {
                try {
                    sendBotMessage(
                        `O chamado do veículo ${occurrence.vehicleName ?? ''} com chassi ${occurrence.vehicleChassis ?? ''} está na etapa: ${OccurrenceStepLabelEnum[toStepEnum] ?? to}`,
                        occurrence.createdByUserProfileId
                    );
                } catch (error) {
                    console.error("Erro ao enviar mensagem:", error);
                }
            }

            return updatedOccurrence;
        } catch (error: any) {
            console.error("Erro ao atualizar:", error);
            throw error;
        }
    }

    const isValidStep = (requiredFields: string[], occurrence: Occurrence): boolean => {
        if (!occurrence) return false;

        // Validate failures
        if (occurrence.failures && occurrence.failures.length > 0) {
            const missingFailures = occurrence.failures.filter(failure =>
                failure.failureType === undefined || failure.failureType === null || failure.failureType === "undefined"
            );
            if (missingFailures.length > 0) return false;
        }

        // Validate parts order list
        if (occurrence.partOrderParts && occurrence.partOrderParts.length > 0) {
            const missingParts = occurrence.partOrderParts.filter(part => 
                part.partNumber === undefined || part.partNumber === null || part.partNumber === "" ||
                part.quantity === undefined || part.quantity === null || part.quantity === ""
            );
            if (missingParts.length > 0) return false;
        }

        return requiredFields.every(field => {
            // Validate array attributes
            if (field === 'failures') return occurrence.failures && occurrence.failures?.length > 0;
            if (field === 'modules') return occurrence.modules && occurrence.modules?.length > 0;
            if (field === 'hasLink') return occurrence.hasLink !== undefined && occurrence.hasLink !== null;
            if (field === 'mainOccurrence') {
                if (occurrence.hasLink !== true) return true;
                return occurrence.mainOccurrence !== undefined && occurrence.mainOccurrence !== null;
            }
            if (field === 'partOrders') {
                if (!occurrence.partOrders || occurrence.partOrders.length === 0) return false;
                return occurrence.partOrders.every(partOrder =>
                    partOrder.number !== undefined && partOrder.number !== null && partOrder.number !== "" &&
                    partOrder.date !== undefined && partOrder.date !== null
                );
            }
            if (field === 'partOrderParts') return occurrence.partOrderParts && occurrence.partOrderParts?.length > 0;

            if (field === 'vehicleModel') {
                const hasModelId = occurrence.vehicleModel !== undefined && occurrence.vehicleModel !== null && occurrence.vehicleModel !== "";
                const hasModelName = occurrence.model !== undefined && occurrence.model !== null && occurrence.model !== "";
                return hasModelId || hasModelName;
            }

            const value = occurrence[field as keyof Occurrence];
            return value !== undefined && value !== null && value !== "";
        });
    }

    const getNotFilledRequiredFields = (requiredFields: string[], occurrence: Occurrence): string[] => {
        let missingFields: string[] = [];

        // Validate failures list
        if (occurrence.failures && occurrence.failures.length > 0) {
            const missingFailures = occurrence.failures.filter(failure =>
                failure.failureType === undefined || failure.failureType === null || failure.failureType === "undefined"
            );
            if (missingFailures.length > 0) missingFields.push("failures");
        }

        // Validate parts order list
        if (occurrence.partOrderParts && occurrence.partOrderParts.length > 0) {
            const missingParts = occurrence.partOrderParts.filter(part => 
                part.partNumber === undefined || part.partNumber === null || part.partNumber === "" ||
                part.quantity === undefined || part.quantity === null || part.quantity === ""
            );
            if (missingParts.length > 0) missingFields.push("partOrderParts");
        }

        missingFields.push(...requiredFields.filter(field => {
            // Validate array attributes
            if (field === 'failures') return !(occurrence.failures && occurrence.failures?.length > 0);
            if (field === 'modules') return !(occurrence.modules && occurrence.modules?.length > 0);
            if (field === 'hasLink') return occurrence.hasLink === undefined || occurrence.hasLink === null;
            if (field === 'mainOccurrence') {
                if (occurrence.hasLink !== true) return false;
                return occurrence.mainOccurrence === undefined || occurrence.mainOccurrence === null;
            }
            if (field === 'partOrders') {
                if (!occurrence.partOrders || occurrence.partOrders.length === 0) return true;
                return !occurrence.partOrders.every(partOrder =>
                    partOrder.number !== undefined && partOrder.number !== null && partOrder.number !== "" &&
                    partOrder.date !== undefined && partOrder.date !== null
                );
            }
            if (field === 'partOrderParts') return !(occurrence.partOrderParts && occurrence.partOrderParts?.length > 0);

            // Validação especial para modelo do veículo: aceita OU vehicleModel (ID) OU model (nome)
            if (field === 'vehicleModel') {
                const hasModelId = occurrence.vehicleModel !== undefined && occurrence.vehicleModel !== null && occurrence.vehicleModel !== "";
                const hasModelName = occurrence.model !== undefined && occurrence.model !== null && occurrence.model !== "";
                return !(hasModelId || hasModelName);
            }

            const value = occurrence[field as keyof Occurrence];
            return value === undefined || value === null || value === "";
        }));

        return missingFields;
    }

    const upsertDispatch = async (occurrence: Occurrence, dispatch: OccurrenceDispatch): Promise<{ wasCreated: boolean, error?: string, dispatch?: OccurrenceDispatch }> => {
        setLoading(true);
        try {
            const dispatchCreated = dispatch.id ? await updateOccurrenceDispatch(occurrence.id, dispatch) : await createOccurrenceDispatch(occurrence.id, dispatch);
            if (dispatchCreated) {
                return { wasCreated: true, dispatch: dispatchCreated };
            } else {
                setLoading(false);
                return { wasCreated: false, error: "Erro ao criar acionamento" };
            }
        } catch (error: any) {
            console.error("Erro ao criar acionamento:", error);
            setLoading(false);
            return { wasCreated: false, error: error };
        }
    }

    const cancelDispatch = async (occurrence: Occurrence, dispatch: OccurrenceDispatch, reason: string, description: string): Promise<{ wasCanceled: boolean, error?: string, dispatch?: OccurrenceDispatch }> => {
        setLoading(true);
        try {
            const dispatchCanceled = await cancelOccurrenceDispatch(occurrence.id, dispatch.id!, { reason, description });
            if (dispatchCanceled) {
                return { wasCanceled: true, dispatch: dispatchCanceled };
            } else {
                setLoading(false);
                return { wasCanceled: false, error: "Erro ao cancelar acionamento" };
            }
        } catch (error: any) {
            console.error("Erro ao cancelar acionamento:", error);
            setLoading(false);
            return { wasCanceled: false, error: error };
        }
    }

    const removeDispatch = async (occurrence: Occurrence, dispatch: OccurrenceDispatch) => {
        setLoading(true);
        try {
            await removeOccurrenceDispatch(occurrence.id, dispatch.id!);
        } catch (error: any) {
            console.error("Erro ao remover acionamento:", error);
            setLoading(false);
        }
    }

    const create = async (occurrence: Occurrence): Promise<{ wasCreated: boolean, error?: string, occurrenceId?: string }> => {
        setLoading(true);
        try {
            occurrence.steps = occurrence.steps?.map(step => {
                if (step.step === OccurrenceStepEnum.CALL) {
                    return {
                        ...step,
                        dtStart: new Date(),
                    };
                }
                return step;
            });

            let user;
            const userId = userProfile?.sub?.split(":")[1];
            if (userId) user = await fetchUser(userId);

            occurrence.createdBy = user?.name;

            const { data, error } = await createOccurrence(occurrence);
            if (error) throw error;
            await reloadOccurrenceList(undefined, occurrence.id);
            setLoading(false);
            return { wasCreated: !!data, occurrenceId: data?.id };
        } catch (error: any) {
            console.error("Erro ao criar:", error);
            setLoading(false);
            return { wasCreated: false, error: error };
        }
    }

    const save = async (occurrence: Occurrence): Promise<boolean> => {
        try {
            const occurrenceUpdated = await updateOccurrence(occurrence);
            if (!occurrenceUpdated) return false;

            setOccurrenceList([
                ...occurrenceList
                    .map(occurrenceEl => occurrenceEl.id === occurrence.id ? occurrenceUpdated : occurrenceEl),
            ]);
            return !!occurrenceUpdated;
        } catch (error: any) {
            console.error("Erro ao salvar:", error);
            return false;
        }
    }

    const finish = async (
        occurrence: Occurrence, 
        finalizationReasonType?: string, 
        finalizationReasonDescription?: string,
        fromStepId?: number,
    ): Promise<boolean> => {
        try {
            await finishOccurrence(occurrence, finalizationReasonType, finalizationReasonDescription, fromStepId);

            // Blockchain - Sign occurrence
            await signRevision({
                id: occurrence.id,
                reviewDate: new Date(),
                reviewName: occurrence.scheduleNumber ?? '',
                chassis: occurrence.vehicleChassis ?? '',
                dealerId: occurrence.dealershipDn ?? '',
                dealerName: occurrence.dealershipName ?? '',
                odometer: occurrence.vehicleOdometer ?? 0,
                accountId: userProfile?.account ?? ''
            });

            await reloadOccurrenceList(occurrence.id);
            return true;
        } catch (error: any) {
            console.error("Erro ao concluir ocorrência:", error);
            throw error;
        }
    }

    /**
     * Review
     */
    const [review, setReview] = useState<OccurrenceReview[]>([]);

    const getReview = async (occurrenceId: string) => {
        const response = await getOccurrenceReview(occurrenceId);
        setReview(response);
    }

    const reviewStep = async (occurrenceId: string, step: OccurrenceStepEnum, rate: number, comment?: string) => {
        await reviewOccurrenceStep(occurrenceId, { step, rate, comment });
        await getReview(occurrenceId);
    }

    return {
        viewType,
        setViewType,
        searchByChassis,
        chassisFilter,
        setChassisListFilter,
        setCriticalityFilter,
        criticalityFilter,
        typeFilter,
        setTypeFilter,
        setStartDateFilter,
        startDateFilter,
        setUpdateDateFilter,
        setIsAlertFilter,
        updateDateFilter,
        finishedFilter,
        setFinishedFilter,
        setDealershipFilter,
        dealershipFilter,
        regionalFilter,
        setRegionalFilter,
        cellFilter,
        setCellFilter,
        cpvNameFilter,
        setCpvNameFilter,
        setStepFilter,
        chassisListFilter,
        occurrenceList,
        occurrenceWithTimeSpentList,
        finalizedOccurrenceWithTimeSpentList,
        rawCurrentOccurrenceWithTimeSpentList,
        firstLoad,
        loading,
        setLoading,
        isValidStep,
        getNotFilledRequiredFields,
        upsertDispatch,
        cancelDispatch,
        removeDispatch,
        create,
        save,
        changeStep,
        finish,
        loadingMoreData,
        getOccurrenceList,
        refreshCurrentData,
        review,
        getReview,
        reviewStep,
        occurrenceStepAvailable,
        fetchOccurrencePage,
        resetPaging,
        stepFilter,
        totalOccurrences,
        totalInProgress,
        steps,
        chassisListFilterByStep,
        setChassisListFilterByStep,
        setStepTypeCurrentFilter,
    }
}

export default useOccurrence; 